import * as ExcelJS from "exceljs";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";
import { OF } from "../../../of/models/of.model";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  border: "FFD0D5DD",
  warnBg: "FFFEE7E6",
  okBg: "FFE6FFF5",
  topBg: "FFE0F2FE",
  rawHeader: "FFE5E7EB",
  titleColor: "FF1B2A4A",
};

interface Row {
  ligneDenomination: string;
  score: string;
  score_poste: string;
}

interface Stats {
  total: number;
  avgScore: number;
  topLabel: string;
  topScore: number;
  worstLabel: string;
  worstScore: number;
  lowPerf: number;
}

export interface ProductiviteExcelData {
  rows: Row[];
  ofs?: OF[];
  stats: Stats;
}

export async function exportProductiviteExcel(
  data: ProductiviteExcelData,
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, { title: "Productivité moyenne" });

  await buildSyntheseSheet(wb, data);
  await buildLignesSheet(wb, data);
  await buildDistributionSheet(wb, data);
  await buildOfsParLigneSheet(wb, data);
  await buildRawSheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `productivite_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildSyntheseSheet(
  wb: ExcelJS.Workbook,
  data: ProductiviteExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Synthèse");
  ws.columns = [{ width: 36 }, { width: 28 }, { width: 12 }];

  setSheetTitle(ws, "Productivité moyenne — depuis le 1er janvier", 3);
  await addJustupBranding(wb, ws, 3);

  let row = 3;
  ws.getCell(row, 1).value = "Indicateurs clés";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;

  const sorted = [...data.rows].sort(
    (a, b) => Number(b.score) - Number(a.score),
  );
  const top3 = sorted.slice(0, 3);
  const bottom3 = [...sorted].slice(-3).reverse();

  const kpis: Array<[string, string | number]> = [
    ["Lignes analysées", data.stats.total],
    ["Score moyen (toutes lignes)", `${data.stats.avgScore.toFixed(0)} %`],
    ["Meilleure ligne", `${data.stats.topLabel} — ${data.stats.topScore} %`],
    [
      "Ligne la plus faible",
      `${data.stats.worstLabel} — ${data.stats.worstScore} %`,
    ],
    ["Lignes en sous-performance (poste ≤ 2)", data.stats.lowPerf],
  ];
  for (const [label, value] of kpis) {
    ws.getCell(row, 1).value = label;
    ws.getCell(row, 1).font = { bold: true };
    ws.getCell(row, 2).value = value;
    row++;
  }

  // Top 3
  row += 1;
  ws.getCell(row, 1).value = "Top 3";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  styleHeader(ws.getRow(row), 3, ["#", "Ligne", "Score"]);
  row++;
  top3.forEach((r, i) => {
    ws.getCell(row, 1).value = i + 1;
    ws.getCell(row, 2).value = r.ligneDenomination;
    ws.getCell(row, 3).value = `${r.score} %`;
    ws.getCell(row, 2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.topBg },
    };
    row++;
  });

  // Bottom 3
  row += 1;
  ws.getCell(row, 1).value = "Bottom 3";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  styleHeader(ws.getRow(row), 3, ["#", "Ligne", "Score"]);
  row++;
  bottom3.forEach((r, i) => {
    ws.getCell(row, 1).value = i + 1;
    ws.getCell(row, 2).value = r.ligneDenomination;
    ws.getCell(row, 3).value = `${r.score} %`;
    ws.getCell(row, 2).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: C.warnBg },
    };
    row++;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildLignesSheet(
  wb: ExcelJS.Workbook,
  data: ProductiviteExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Lignes");
  ws.columns = [
    { key: "ligne", width: 32 },
    { key: "score", width: 14 },
    { key: "score_poste", width: 22 },
    { key: "statut", width: 18 },
    { key: "delta", width: 14 },
  ];
  setSheetTitle(ws, "Productivité — détail par ligne", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Ligne",
    "Score (%)",
    "Évaluation poste (/5)",
    "Statut",
    "Δ vs moyenne",
  ]);

  for (const row of data.rows) {
    const sp = Number(row.score_poste);
    const isWarn = sp <= 2;
    const score = Number(row.score);
    const delta = score - data.stats.avgScore;
    const r = ws.addRow({
      ligne: row.ligneDenomination,
      score: score,
      score_poste: sp,
      statut: isWarn ? "Sous-performance" : "OK",
      delta: Math.round(delta),
    });
    r.getCell("score").numFmt = "0";
    r.getCell("score_poste").numFmt = "0";
    r.getCell("statut").fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: isWarn ? C.warnBg : C.okBg },
    };
    if (delta < -10) {
      r.getCell("delta").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.warnBg },
      };
    } else if (delta > 10) {
      r.getCell("delta").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: C.okBg },
      };
    }
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildDistributionSheet(
  wb: ExcelJS.Workbook,
  data: ProductiviteExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Distribution");
  ws.columns = [
    { key: "bucket", width: 22 },
    { key: "count", width: 14 },
    { key: "pct", width: 12 },
    { key: "names", width: 60 },
  ];
  setSheetTitle(ws, "Productivité — distribution des scores", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Tranche de score",
    "Nb lignes",
    "% lignes",
    "Lignes concernées",
  ]);

  const buckets: Array<{
    label: string;
    min: number;
    max: number;
    ligns: string[];
  }> = [
    { label: "0–25 %", min: 0, max: 25, ligns: [] },
    { label: "25–50 %", min: 25, max: 50, ligns: [] },
    { label: "50–75 %", min: 50, max: 75, ligns: [] },
    { label: "75–100 %", min: 75, max: 100, ligns: [] },
    { label: "> 100 %", min: 100, max: Infinity, ligns: [] },
  ];
  for (const r of data.rows) {
    const score = Number(r.score);
    const b = buckets.find((x) => score >= x.min && score < x.max);
    if (b) b.ligns.push(r.ligneDenomination);
    else buckets[buckets.length - 1].ligns.push(r.ligneDenomination);
  }
  const total = data.rows.length;
  for (const b of buckets) {
    if (b.ligns.length === 0) continue;
    const r = ws.addRow({
      bucket: b.label,
      count: b.ligns.length,
      pct: total > 0 ? b.ligns.length / total : 0,
      names: b.ligns.join(", "),
    });
    r.getCell("pct").numFmt = "0.0%";
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildOfsParLigneSheet(
  wb: ExcelJS.Workbook,
  data: ProductiviteExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("OFs par ligne");
  ws.columns = [
    { key: "ligne", width: 24 },
    { key: "numero", width: 14 },
    { key: "article", width: 32 },
    { key: "code", width: 14 },
    { key: "dateDebut", width: 14 },
    { key: "qty", width: 14 },
    { key: "qtyProd", width: 14 },
    { key: "score", width: 14 },
    { key: "scoreMat", width: 14 },
    { key: "scoreP", width: 16 },
    { key: "scorePostes", width: 18 },
  ];
  setSheetTitle(ws, "Productivité — détail des OFs par ligne", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  styleHeader(ws.getRow(2), ws.columns.length, [
    "Ligne",
    "Numéro OF",
    "Article",
    "Code article",
    "Date début",
    "Qté prévue",
    "Qté produite",
    "Score total",
    "Score matière",
    "Score personnel",
    "Score postes (moy.)",
  ]);

  const ofs = data.ofs ?? [];
  const yearStart = new Date(new Date().getFullYear(), 0, 1);
  const sorted = [...ofs]
    .filter(
      (o) =>
        o.ligne?.denomination &&
        (o as any).scoreTotal != null &&
        o.dateDebut &&
        new Date(o.dateDebut as any) >= yearStart,
    )
    .sort((a, b) => {
      const cmp = (a.ligne?.denomination ?? "").localeCompare(
        b.ligne?.denomination ?? "",
      );
      if (cmp !== 0) return cmp;
      return (
        new Date(b.dateDebut as any).getTime() -
        new Date(a.dateDebut as any).getTime()
      );
    });

  for (const o of sorted) {
    const postes = (o as any).postes ?? [];
    const scoredPostes = postes.filter(
      (p: any) => p.score != null && p.score !== "",
    );
    const moyPostes =
      scoredPostes.length > 0
        ? scoredPostes.reduce((s: number, p: any) => s + Number(p.score), 0) /
          scoredPostes.length
        : null;
    const r = ws.addRow({
      ligne: o.ligne?.denomination ?? "",
      numero: o.numero ?? "",
      article: o.articleInfos?.denomination ?? "",
      code: o.articleInfos?.code ?? "",
      dateDebut: o.dateDebut ? new Date(o.dateDebut as any) : null,
      qty: o.quantite ?? "",
      qtyProd: (o as any).quantiteProduite ?? "",
      score: roundOrEmpty((o as any).scoreTotal),
      scoreMat: roundOrEmpty((o as any).scoreMatiere),
      scoreP: roundOrEmpty((o as any).scorePersonnel),
      scorePostes: moyPostes != null ? Number(moyPostes.toFixed(1)) : "",
    });
    r.getCell("dateDebut").numFmt = "dd/mm/yyyy";
    r.getCell("qty").numFmt = "#,##0.##";
    r.getCell("qtyProd").numFmt = "#,##0.##";
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
async function buildRawSheet(
  wb: ExcelJS.Workbook,
  data: ProductiviteExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Données brutes");
  ws.columns = [
    { key: "id", width: 38 },
    { key: "numero", width: 14 },
    { key: "etat", width: 22 },
    { key: "dateDebut", width: 14 },
    { key: "dateFin", width: 14 },
    { key: "quantite", width: 12 },
    { key: "qtyProd", width: 16 },
    { key: "ligne", width: 22 },
    { key: "article", width: 32 },
    { key: "code", width: 14 },
    { key: "scoreTotal", width: 12 },
    { key: "scoreMatiere", width: 12 },
    { key: "scorePersonnel", width: 14 },
    { key: "scoreMatSurcout", width: 18 },
    { key: "scorePersSurcout", width: 20 },
    { key: "nbPostes", width: 10 },
    { key: "nbPostesScored", width: 14 },
    { key: "scorePostes", width: 18 },
    { key: "valideProd", width: 12 },
    { key: "valideQualite", width: 14 },
    { key: "postes", width: 60 },
  ];
  setSheetTitle(ws, "Données brutes — tous les OFs source", ws.columns.length);
  await addJustupBranding(wb, ws, ws.columns.length);
  const headerRow = ws.getRow(2);
  headerRow.values = [
    "OF.id",
    "OF.numero",
    "OF.etat",
    "OF.dateDebut",
    "OF.dateFin",
    "OF.quantite",
    "OF.quantiteProduite",
    "ligne.denomination",
    "articleInfos.denomination",
    "articleInfos.code",
    "scoreTotal",
    "scoreMatiere",
    "scorePersonnel",
    "scoreMatiereSurcout",
    "scorePersonnelSurcout",
    "Nb postes",
    "Nb postes notés",
    "Score postes (moy.)",
    "valideProd",
    "valideQualite",
    "Détail postes",
  ];
  headerRow.eachCell((c) => {
    c.font = { bold: true, size: 11 };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.rawHeader } };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  });

  for (const o of data.ofs ?? []) {
    const postes = (o as any).postes ?? [];
    // On ne calcule la moyenne et on n'affiche le détail que sur les postes
    // qui ont effectivement un score. ~34 % des OFs ont des postes non notés
    // → moyenne pondérée par 0 incohérente sinon, et détail pollué par des
    // « Opé. X: — » qui n'apportent rien.
    const scoredPostes = postes.filter(
      (p: any) => p.score != null && p.score !== "",
    );
    const moyPostes =
      scoredPostes.length > 0
        ? scoredPostes.reduce((s: number, p: any) => s + Number(p.score), 0) /
          scoredPostes.length
        : null;
    const detailPostes = scoredPostes.length
      ? scoredPostes
          .map((p: any) => `${p.denomination ?? p.id ?? "?"}: ${p.score}`)
          .join(" | ")
      : "";
    const r = ws.addRow({
      id: o.id ?? "",
      numero: o.numero ?? "",
      etat: o.etat ?? "",
      dateDebut: o.dateDebut ? new Date(o.dateDebut as any) : null,
      dateFin: (o as any).dateFin ? new Date((o as any).dateFin) : null,
      quantite: o.quantite ?? "",
      qtyProd: (o as any).quantiteProduite ?? "",
      ligne: o.ligne?.denomination ?? "",
      article: o.articleInfos?.denomination ?? "",
      code: o.articleInfos?.code ?? "",
      scoreTotal: roundOrEmpty((o as any).scoreTotal),
      scoreMatiere: roundOrEmpty((o as any).scoreMatiere),
      scorePersonnel: roundOrEmpty((o as any).scorePersonnel),
      scoreMatSurcout: roundOrEmpty((o as any).scoreMatiereSurcout),
      scorePersSurcout: roundOrEmpty((o as any).scorePersonnelSurcout),
      nbPostes: postes.length,
      nbPostesScored: scoredPostes.length,
      scorePostes: moyPostes != null ? Number(moyPostes.toFixed(1)) : "",
      valideProd: (o as any).valideProd ?? "",
      valideQualite: (o as any).valideQualite ?? "",
      postes: detailPostes,
    });
    r.getCell("dateDebut").numFmt = "dd/mm/yyyy";
    r.getCell("dateFin").numFmt = "dd/mm/yyyy";
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: ws.columns.length },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
function setSheetTitle(
  ws: ExcelJS.Worksheet,
  title: string,
  ncols: number,
): void {
  const row = ws.getRow(1);
  row.height = 22;
  ws.mergeCells(1, 1, 1, Math.max(1, ncols - 1));
  row.getCell(1).value = title;
  row.getCell(1).font = { bold: true, size: 14, color: { argb: C.titleColor } };
  row.getCell(1).alignment = { vertical: "middle" };
}

function styleHeader(
  row: ExcelJS.Row,
  ncols: number,
  values?: string[],
): void {
  if (values) {
    for (let i = 0; i < values.length; i++) {
      row.getCell(i + 1).value = values[i];
    }
  }
  row.height = 20;
  for (let i = 1; i <= ncols; i++) {
    const c = row.getCell(i);
    c.font = { bold: true, color: { argb: C.headerFont } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
    c.alignment = { vertical: "middle" };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  }
}

function roundOrEmpty(v: any): number | string {
  const n = Number(v);
  if (!isFinite(n)) return "";
  return Math.round(n * 100) / 100;
}
